import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./bop020w010.css";
import BOP020W010Template from "./bop020w010.template";
import {
  EXPENSE_TYPE,
  INCOME_TYPE,
  type Category,
  normalizeCategoryLarge,
  normalizeType,
} from "../../common/commonConstant";
import useApiRequest from "../../common/useApiRequest";
import type { ReturnTo } from "../navigationState";
import { useMaster } from "../../common/MasterContext";
import {
  buildExpenseStatusKey,
  MAX_DISPLAY_COUNT,
  type ExpenseStatusEntry,
} from "../../common/expenseStatus";

export interface ItemRatio {
  key: string;
  label: string;
  // 目標限度額に対する当月支出の割合（%）。目標限度額が0の場合はnull
  ratio: number | null;
}

/**
 * 機能ID: BOP020W010
 * 機能名: 収支登録画面
 */
const SUMMARY_CATEGORY = "合計";
// DB上のtypeは「収入/支出」と「income/expense」が混在しているため両方を対象とする
// （normalizeTypeは未知の値を支出扱いするため、事前にこれ以外の値を除外する）
const KNOWN_TYPES = [INCOME_TYPE, EXPENSE_TYPE, "income", "expense"];

const BOP020W010 = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [shushiNo, setShushiNo] = useState("");
  const [type, setType] = useState<Category>(EXPENSE_TYPE);
  const [categoryLarge, setCategoryLarge] = useState("");
  const [categorySmall, setCategorySmall] = useState("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  });
  const [memo, setMemo] = useState("");
  const [keihiFlg, setKeihiFlg] = useState<0 | 1>(0);
  const [mikakuteiFlg, setMikakuteiFlg] = useState<0 | 1>(0);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  // ONの場合、新規登録後に金額以外の入力内容を保持する（クリアでは解除しない）
  const [keepInputFlg, setKeepInputFlg] = useState(false);
  const { isLoading, execute } = useApiRequest();
  // 支出割合の取得は登録処理とは独立させ、ローディングオーバーレイを表示しない
  const { execute: executeSummary } = useApiRequest();
  const [expenseRatio, setExpenseRatio] = useState<number | null>(null);

  // 現在日付の月の支出割合（支出合計 / 収入合計 × 100）を月次集計APIから算出する。
  // 収入が0、または取得に失敗した場合はnull（未算出）とする。
  const fetchExpenseRatio = useCallback(async () => {
    const now = new Date();
    const month = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}`;
    const result = await executeSummary(
      `${process.env.REACT_APP_API_BASE_URL}/api/bop020rs03001/search`,
      {
        method: "POST",
        body: JSON.stringify({ month }),
      },
    );
    if (!result.ok || !Array.isArray(result.data)) {
      setExpenseRatio(null);
      return;
    }

    const rows: any[] = result.data;
    const getTotal = (type: Category) =>
      Math.abs(
        rows
          .filter(
            (row: any) =>
              row.categoryLarge === SUMMARY_CATEGORY &&
              KNOWN_TYPES.includes(row.type) &&
              normalizeType(row.type) === type,
          )
          .reduce((sum: number, row: any) => sum + (Number(row.amount) || 0), 0),
      );
    const income = getTotal(INCOME_TYPE);
    const expense = getTotal(EXPENSE_TYPE);
    setExpenseRatio(income > 0 ? (expense / income) * 100 : null);
  }, [executeSummary]);

  // 項目管理画面で「登録画面に表示」とした支出項目の、目標限度額に対する当月支出の割合
  const { categorySmallList } = useMaster();
  const { execute: executeExpenseStatus } = useApiRequest<ExpenseStatusEntry[]>();
  const [displayedStatuses, setDisplayedStatuses] = useState<ExpenseStatusEntry[]>([]);

  const fetchItemRatios = useCallback(async () => {
    const result = await executeExpenseStatus(
      `${process.env.REACT_APP_API_BASE_URL}/api/bop030rs03001/status`,
    );
    if (!result.ok || !Array.isArray(result.data)) {
      setDisplayedStatuses([]);
      return;
    }
    setDisplayedStatuses(
      result.data.filter((s) => s.displayFlg === 1 && s.targetAmount !== null),
    );
  }, [executeExpenseStatus]);

  // 表示順は汎用マスタ（項目管理画面）の項目並び順に倣う
  const itemRatios: ItemRatio[] = useMemo(() => {
    const orderMap = new Map<string, number>();
    categorySmallList.forEach((item, index) => {
      const key = buildExpenseStatusKey(item.type, item.category, item.label);
      if (!orderMap.has(key)) orderMap.set(key, index);
    });
    const getOrder = (key: string) => orderMap.get(key) ?? Number.MAX_SAFE_INTEGER;

    return displayedStatuses
      .map((s) => {
        const key = buildExpenseStatusKey(s.type, s.categoryLarge, s.categorySmall);
        const target = Number(s.targetAmount);
        return {
          key,
          label: s.categorySmall,
          ratio:
            target > 0
              ? (Math.abs(Number(s.currentMonthAmount) || 0) / target) * 100
              : null,
        };
      })
      .sort((a, b) => getOrder(a.key) - getOrder(b.key))
      .slice(0, MAX_DISPLAY_COUNT);
  }, [displayedStatuses, categorySmallList]);

  const refreshRatios = useCallback(() => {
    fetchExpenseRatio();
    fetchItemRatios();
  }, [fetchExpenseRatio, fetchItemRatios]);

  useEffect(() => {
    refreshRatios();
  }, [refreshRatios]);

  useEffect(() => {
    const state = location.state as
      | { record?: any; returnTo?: ReturnTo }
      | null;
    if (state?.record) {
      const record = state.record;
      setShushiNo(record.shushiNo || "");
      setType(normalizeType(record.type));
      setCategoryLarge(normalizeCategoryLarge(record.categoryLarge || ""));
      setCategorySmall(record.categorySmall || "");
      setAmount(record.amount != null ? String(record.amount) : "");
      setDate(record.date || "");
      setMemo(record.memo || "");
      setKeihiFlg(record.keihiFlg === 1 || record.keihiFlg === true ? 1 : 0);
      setMikakuteiFlg(record.mikakuteiFlg === 1 || record.mikakuteiFlg === true ? 1 : 0);
      setEditingId(record.shushiNo ?? null);
      setIsEditing(true);
    }
  }, [location.state]);

  const handleTypeChange = (newType: Category) => {
    setType(newType);
    setCategoryLarge("");
    setCategorySmall("");
  };

  const handleCategoryLargeChange = (value: string) => {
    setCategoryLarge(value);
    setCategorySmall("");
  };

  const handleClear = () => {
    setType(EXPENSE_TYPE);
    setCategoryLarge("");
    setCategorySmall("");
    setAmount("");
    setShushiNo("");
    setDate(() => {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    });
    setMemo("");
    setKeihiFlg(0);
    setMikakuteiFlg(0);
    setEditingId(null);
    setIsEditing(false);
  };

  // 収支一覧画面からの遷移（編集）であれば、遷移前の検索条件を復元して一覧画面へ戻す
  const navigateToReturn = (returnTo: ReturnTo) => {
    navigate(returnTo.pathname, {
      state: {
        searchSnapshot: returnTo.searchSnapshot,
        page: returnTo.page,
        sortKey: returnTo.sortKey,
        sortDirection: returnTo.sortDirection,
        autoSearch: true,
        returnTo: returnTo.returnTo,
      },
      replace: true,
    });
  };

  const handleBack = () => {
    const returnTo = (location.state as { returnTo?: ReturnTo } | null)
      ?.returnTo;
    if (returnTo?.pathname) {
      navigateToReturn(returnTo);
      return;
    }
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  // 今月の支出割合クリック時は、月次集計画面へ遷移して当月の集計を自動検索させる
  const handleExpenseRatioClick = () => {
    const now = new Date();
    const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    navigate("/bop020w030", { state: { month, autoSearch: true } });
  };

  const handleSubmit = async () => {
    const missingFields: string[] = [];

    if (!type) missingFields.push("収支");
    if (!categoryLarge) missingFields.push("項目（大）");
    if (!categorySmall) missingFields.push("項目（小）");
    if (!amount.trim()) missingFields.push("金額（円）");
    if (!date) missingFields.push("日付");

    if (missingFields.length > 0) {
      alert(`必須項目を入力してください: ${missingFields.join("、")}`);
      return;
    }

    const payload = {
      shushiNo,
      type,
      categoryLarge,
      categorySmall,
      amount: Number(amount),
      date,
      memo,
      keihiFlg,
      mikakuteiFlg,
    };
    const isUpdate = isEditing;
    const url = editingId
      ? `${process.env.REACT_APP_API_BASE_URL}/api/bop020rs01002`
      : `${process.env.REACT_APP_API_BASE_URL}/api/bop020rs01001`;

    const result = await execute(url, {
      method: "POST",
      body: JSON.stringify(payload),
    });

    if (result.ok) {
      alert(isUpdate ? "更新成功" : "登録成功");
      refreshRatios();
      const returnTo = (location.state as { returnTo?: ReturnTo } | null)
        ?.returnTo;
      if (isUpdate && returnTo?.pathname) {
        navigateToReturn(returnTo);
        return;
      }
      if (!isUpdate && keepInputFlg) {
        setAmount("");
        return;
      }
      handleClear();
    } else {
      alert(result.error.message);
    }
  };

  const handleDelete = async () => {
    if (!shushiNo) {
      alert("削除対象の収支番号がありません");
      return;
    }

    const result = await execute(
      `${process.env.REACT_APP_API_BASE_URL}/api/bop020rs01003`,
      {
        method: "POST",
        body: JSON.stringify({ shushiNo }),
      },
    );

    if (result.ok) {
      alert("削除成功");
      refreshRatios();
      handleClear();
    } else {
      alert(result.error.message);
    }
  };

  return (
    <BOP020W010Template
      type={type}
      categoryLarge={categoryLarge}
      categorySmall={categorySmall}
      amount={amount}
      date={date}
      memo={memo}
      keihiFlg={keihiFlg}
      mikakuteiFlg={mikakuteiFlg}
      keepInputFlg={keepInputFlg}
      isEditing={isEditing}
      isLoading={isLoading}
      expenseRatio={expenseRatio}
      itemRatios={itemRatios}
      submitButtonLabel={isEditing ? "更新" : "登録"}
      onTypeChange={handleTypeChange}
      onCategoryLargeChange={handleCategoryLargeChange}
      onCategorySmallChange={setCategorySmall}
      onAmountChange={setAmount}
      onDateChange={setDate}
      onMemoChange={setMemo}
      onKeihiFlgChange={(checked) => setKeihiFlg(checked ? 1 : 0)}
      onMikakuteiFlgChange={(checked) => setMikakuteiFlg(checked ? 1 : 0)}
      onKeepInputFlgChange={setKeepInputFlg}
      onClear={handleClear}
      onBack={handleBack}
      onDelete={handleDelete}
      onSubmit={handleSubmit}
      onExpenseRatioClick={handleExpenseRatioClick}
    />
  );
};

export default BOP020W010;
